import { GeoJsonLayer } from '@deck.gl/layers';
import proj4 from 'proj4';
import DxfParser from 'dxf-parser';
import bbox from '@turf/bbox';
import { showMapConfirmToast } from "../utils/Toast/mapToast";

const transformCoordinates = (coords, sourceCRS, targetCRS) => {
    return coords.map(coord => {
        try {
            const [x, y] = coord;
            const [lon, lat] = proj4(sourceCRS, targetCRS, [x, y]);
            return [lon, lat];
        } catch (error) {
            console.error('Error transforming coordinates:', error);
            return coord;
        }
    });
};

const generateArcPoints = (center, radius, startAngle, endAngle, numPoints = 200) => {
    const points = [];
    const angleStep = (endAngle - startAngle) / numPoints;

    for (let i = 0; i <= numPoints; i++) {
        const angle = startAngle + i * angleStep;
        const x = center[0] + radius * Math.cos(angle);
        const y = center[1] + radius * Math.sin(angle);
        points.push([x, y]);
    }

    return points;
};

const generateArcSegments = (center, radius, startAngle, endAngle, segmentCount = 10) => {
    const points = generateArcPoints(center, radius, startAngle, endAngle);
    const segments = [];
    const segmentLength = Math.floor(points.length / segmentCount);

    for (let i = 0; i < segmentCount; i++) {
        const segmentStart = i * segmentLength;
        const segmentEnd =
            i === segmentCount - 1 ? points.length - 1 : (i + 1) * segmentLength;

        const segmentPoints = points.slice(segmentStart, segmentEnd + 1);
        segments.push(segmentPoints);
    }

    return segments;
};

const generateEllipsePoints = (center, a, b, numPoints = 100) => {
    const points = [];

    for (let i = 0; i < numPoints; i++) {
        const angle = (i / numPoints) * 2 * Math.PI;
        const x = center[0] + a * Math.cos(angle);
        const y = center[1] + b * Math.sin(angle);
        points.push([x, y]);
    }

    return points;
};

const generateEllipseSegments = (center, a, b, segmentCount = 10) => {
    const points = generateEllipsePoints(center, a, b);
    const segments = [];
    const segmentLength = Math.floor(points.length / segmentCount);

    for (let i = 0; i < segmentCount; i++) {
        const segmentStart = i * segmentLength;
        const segmentEnd =
            i === segmentCount - 1 ? points.length - 1 : (i + 1) * segmentLength;

        const segmentPoints = points.slice(segmentStart, segmentEnd + 1);
        segments.push(segmentPoints);
    }

    return segments;
};

const parseDxfToGeoJson = (dxfData, sourceCRS, targetCRS = 'EPSG:4326') => {
    const parser = new DxfParser();
    const dxf = parser.parseSync(dxfData);

    const layers = {
        lines: [],
        points: [],
        polylines: [],
        polygons: [],
        circles: [],
        arcs: [],
        ellipses: [],
        splines: [],
        texts: [],
    };

    dxf.entities.forEach(entity => {
        switch (entity.type) {
            case 'LINE':
                layers.lines.push({
                    type: 'Feature',
                    geometry: {
                        type: 'MultiLineString',
                        coordinates: [
                            transformCoordinates(
                                [
                                    [entity.vertices[0].x, entity.vertices[0].y],
                                    [entity.vertices[1].x, entity.vertices[1].y],
                                ],
                                sourceCRS,
                                targetCRS
                            ),
                        ],
                    },
                    properties: {},
                });
                break;

            case 'POINT':
                layers.points.push({
                    type: 'Feature',
                    geometry: {
                        type: 'Point',
                        coordinates: transformCoordinates(
                            [[entity.position.x, entity.position.y]],
                            sourceCRS,
                            targetCRS
                        )[0],
                    },
                    properties: {},
                });
                break;

            case 'LWPOLYLINE':
            case 'POLYLINE': {
                const coordinates = transformCoordinates(
                    entity.vertices.map(vertex => [vertex.x, vertex.y]),
                    sourceCRS,
                    targetCRS
                );

                if (entity.closed) {
                    layers.polygons.push({
                        type: 'Feature',
                        geometry: {
                            type: 'Polygon',
                            coordinates: [coordinates],
                        },
                        properties: {},
                    });
                } else {
                    layers.polylines.push({
                        type: 'Feature',
                        geometry: {
                            type: 'MultiLineString',
                            coordinates: [coordinates],
                        },
                        properties: {},
                    });
                }

                break;
            }

            case 'CIRCLE':
                layers.circles.push({
                    type: 'Feature',
                    geometry: {
                        type: 'Point',
                        coordinates: transformCoordinates(
                            [[entity.center.x, entity.center.y]],
                            sourceCRS,
                            targetCRS
                        )[0],
                    },
                    properties: {
                        radius: entity.radius,
                    },
                });
                break;

            case 'ARC': {
                const arcCenter = [entity.center.x, entity.center.y];

                const arcSegments = generateArcSegments(
                    arcCenter,
                    entity.radius,
                    entity.startAngle * (Math.PI / 180),
                    entity.endAngle * (Math.PI / 180)
                );

                const transformedArcSegments = arcSegments.map(segment =>
                    transformCoordinates(segment, sourceCRS, targetCRS)
                );

                transformedArcSegments.forEach(segment => {
                    layers.arcs.push({
                        type: 'Feature',
                        geometry: {
                            type: 'LineString',
                            coordinates: segment,
                        },
                        properties: {
                            radius: entity.radius,
                            startAngle: entity.startAngle,
                            endAngle: entity.endAngle,
                        },
                    });
                });

                break;
            }

            case 'ELLIPSE': {
                const ellipseCenter = [entity.center.x, entity.center.y];

                const ellipseSegments = generateEllipseSegments(
                    transformCoordinates([ellipseCenter], sourceCRS, targetCRS)[0],
                    entity.majorAxisLength / 2,
                    entity.minorAxisLength / 2,
                    10
                );

                ellipseSegments.forEach(segment => {
                    layers.ellipses.push({
                        type: 'Feature',
                        geometry: {
                            type: 'LineString',
                            coordinates: segment,
                        },
                        properties: {
                            majorAxis: entity.majorAxisLength,
                            minorAxis: entity.minorAxisLength,
                            rotation: entity.rotation,
                        },
                    });
                });

                break;
            }

            case 'SPLINE':
                layers.splines.push({
                    type: 'Feature',
                    geometry: {
                        type: 'LineString',
                        coordinates: transformCoordinates(
                            entity.controlPoints.map(point => [point.x, point.y]),
                            sourceCRS,
                            targetCRS
                        ),
                    },
                    properties: {},
                });
                break;

            case 'TEXT':
                layers.texts.push({
                    type: 'Feature',
                    geometry: {
                        type: 'Point',
                        coordinates: transformCoordinates(
                            [[entity.startPoint.x, entity.startPoint.y]],
                            sourceCRS,
                            targetCRS
                        )[0],
                    },
                    properties: {
                        text: entity.text,
                        height: entity.textHeight,
                        rotation: entity.rotation,
                    },
                });
                break;

            default:
                console.warn('Unsupported entity type:', entity.type);
        }
    });

    return {
        lines: { type: 'FeatureCollection', features: layers.lines },
        points: { type: 'FeatureCollection', features: layers.points },
        polylines: { type: 'FeatureCollection', features: layers.polylines },
        polygons: { type: 'FeatureCollection', features: layers.polygons },
        circles: { type: 'FeatureCollection', features: layers.circles },
        arcs: { type: 'FeatureCollection', features: layers.arcs },
        ellipses: { type: 'FeatureCollection', features: layers.ellipses },
        splines: { type: 'FeatureCollection', features: layers.splines },
        texts: { type: 'FeatureCollection', features: layers.texts },
    };
};

const handleFileOpen = async (file) => {
    try {
        if (!file) throw new Error('No file provided');

        const reader = new FileReader();

        return new Promise((resolve, reject) => {
            reader.onload = () => {
                try {
                    const sourceCRS = 'EPSG:3908';
                    const dxfData = reader.result;
                    const geoJsonLayers = parseDxfToGeoJson(
                        dxfData,
                        sourceCRS,
                        'EPSG:4326'
                    );

                    resolve(geoJsonLayers);
                } catch (error) {
                    console.error('Failed to parse DXF:', error);
                    reject(new Error('Failed to parse DXF'));
                }
            };

            reader.onerror = () => {
                reject(new Error('Failed to read file'));
            };

            reader.readAsText(file);
        });
    } catch (error) {
        console.error('Error processing file:', error);
    }
};

export const loadDXF = async (file) => {
    try {
        const geoJsonLayers = await handleFileOpen(file);
        return geoJsonLayers;
    } catch (error) {
        console.error('Error loading DXF:', error);
        throw error;
    }
};

export const setDXFLayers = (
    layerIndex,
    geoJsonLayers,
    setHoverInfo,
    setWfsLayers,
    setViewState
) => {
    const {
        lines,
        points,
        polylines,
        polygons,
        circles,
        arcs,
        ellipses,
        splines,
        texts,
    } = geoJsonLayers;

    const newLayers = [];

    if (lines.features.length > 0) {
        newLayers.push(
            new GeoJsonLayer({
                id: `dxf-lines-${layerIndex}`,
                data: lines,
                source: "catalog",
                pickable: true,
                stroked: true,
                lineWidthMinPixels: 0,
                getLineWidth: () => 0.1,
                getLineColor: [255, 0, 0, 200],
                onHover: info => setHoverInfo(info),
            })
        );
    }

    if (polylines.features.length > 0) {
        newLayers.push(
            new GeoJsonLayer({
                id: `dxf-polylines-${layerIndex}`,
                data: polylines,
                source: "catalog",
                pickable: true,
                stroked: true,
                lineWidthMinPixels: 2,
                getLineWidth: () => 0.1,
                getLineColor: [0, 255, 0, 200],
                onHover: info => setHoverInfo(info),
            })
        );
    }

    if (polygons.features.length > 0) {
        newLayers.push(
            new GeoJsonLayer({
                id: `dxf-polygons-${layerIndex}`,
                data: polygons,
                source: "catalog",
                pickable: true,
                filled: true,
                stroked: true,
                getFillColor: [0, 255, 0, 100],
                getLineColor: [0, 255, 0, 200],
                onHover: info => setHoverInfo(info),
            })
        );
    }

    if (circles.features.length > 0) {
        newLayers.push(
            new GeoJsonLayer({
                id: `dxf-circles-${layerIndex}`,
                data: circles,
                source: "catalog",
                pickable: true,
                filled: true,
                pointType: 'circle',
                getFillColor: [255, 0, 255, 200],
                getPointRadius: 0.2,
                getLineWidth: () => 0.07,
                onHover: info => setHoverInfo(info),
            })
        );
    }

    if (arcs.features.length > 0) {
        newLayers.push(
            new GeoJsonLayer({
                id: `dxf-arcs-${layerIndex}`,
                data: arcs,
                source: "catalog",
                pickable: true,
                stroked: true,
                lineWidthMinPixels: 2,
                getLineWidth: () => 0.1,
                getLineColor: [255, 0, 0, 200],
                onHover: info => setHoverInfo(info),
            })
        );
    }

    if (ellipses.features.length > 0) {
        newLayers.push(
            new GeoJsonLayer({
                id: `dxf-ellipses-${layerIndex}`,
                data: ellipses,
                source: "catalog",
                pickable: true,
                stroked: true,
                lineWidthMinPixels: 2,
                getLineWidth: () => 0.1,
                getLineColor: [255, 165, 0, 200],
                onHover: info => setHoverInfo(info),
            })
        );
    }

    if (splines.features.length > 0) {
        newLayers.push(
            new GeoJsonLayer({
                id: `dxf-splines-${layerIndex}`,
                data: splines,
                source: "catalog",
                pickable: true,
                stroked: true,
                lineWidthMinPixels: 2,
                getLineColor: [0, 255, 255, 200],
                onHover: info => setHoverInfo(info),
            })
        );
    }

    if (texts.features.length > 0) {
        newLayers.push(
            new GeoJsonLayer({
                id: `dxf-texts-${layerIndex}`,
                data: texts,
                source: "catalog",
                pickable: true,
                pointType: 'text',
                getFillColor: [0, 0, 0, 255],
                getTextSize: () => 10,
                getTextRotation: f => f.properties.rotation || 0,
                onHover: info => setHoverInfo(info),
            })
        );
    }

    setWfsLayers(prev => [...prev, ...newLayers]);

    const allFeatures = [
        ...lines.features,
        ...points.features,
        ...polylines.features,
        ...polygons.features,
        ...circles.features,
        ...arcs.features,
        ...ellipses.features,
        ...splines.features,
        ...texts.features,
    ];

    if (allFeatures.length > 0) {
        const geojson = {
            type: 'FeatureCollection',
            features: allFeatures,
        };

        const [minX, minY, maxX, maxY] = bbox(geojson);
        const centerLon = (minX + maxX) / 2;
        const centerLat = (minY + maxY) / 2;
        const lonRange = maxX - minX;
        const latRange = maxY - minY;
        const zoom = Math.log2(360 / Math.max(lonRange, latRange));

        showMapConfirmToast({
            title: "Pozicioniranje mape",
            message: "Da li želite da se prikaz mape pozicionira prema učitanom DXF sloju?",
            confirmText: "Da",
            cancelText: "Ne",
            variant: "info",
            onConfirm: () => {
                setViewState(prev => ({
                    ...prev,
                    longitude: centerLon,
                    latitude: centerLat,
                    zoom,
                    pitch: 0,
                    bearing: 0,
                    transitionDuration: 1000,
                }));
            },
        });
    }
};